import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";
import { Subscription, BehaviorSubject, of, Observable, forkJoin } from "rxjs";
import { delay, mergeMap, map, finalize } from "rxjs/operators";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { LayoutService } from "../../../layouts/services/layout.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { CanDeactivateComponent } from "../../classes/can-deactivate.component";

import { genId } from "../../../core/services/genId";
import { ApplicationPayment, PendingApplicationPayment, Payment, PaymentStatus, PaymentType, PaymentTypeDisplay, PaymentInstrument, writeToken } from "../../../payments/models/paymentItem.model";
import { SubHeader } from "../../../core/models/sub-header.model";
import { AuthService } from "../../../services/auth.service";
import { CommonDatePickerComponent } from "../common-date-picker/common-date-picker.component";
import { FlowInstanceService } from "../../../flow/services/flow-instance.service";
import { PaymentReceivedDateRequest } from "../../../flow/models/flow.model";
import { LoaderService } from "../../services/loader.service";
import { ApplicationsService } from "../../../applications/services/applications.service";

@Component({
    selector: "payment-processing",
    templateUrl: "./payment-processing.component.html",
    styleUrls: ["./payment-processing.component.scss"],
    standalone: false
})
export class PaymentProcessingComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    applicationList: string[] = [];
    application: string;
    applicationPayments: Map<string, PendingApplicationPayment[]> = new Map<string, PendingApplicationPayment[]>();
    blockPaymentStatuses: any[] = [PaymentStatus.PendingReceipt, PaymentStatus.Paid, PaymentStatus.Overpaid];
    cashFirst: boolean = true;
    currentPaymentTypeId: string;
    itemNumber: string;
    itemsRequiringNumber = [PaymentType.LocalCheck, PaymentType.MoneyOrder];
    organization: string;
    paymentAmount: number;
    subHeader: SubHeader = new SubHeader({ title: "Payment Processing", buttons: [] });
    paymentEntrySection: number = 1;
    paymentsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    paymentTypes: any[] = [{ id: "select", display: "--Select--" }, { id: PaymentType.PaidCash, display: "Cash" }, { id: PaymentType.LocalCheck, display: "Check" }, { id: PaymentType.MoneyOrder, display: "Money Order" }];
    paymentVisible: boolean = true;
    pendingPayments: PendingApplicationPayment[];
    receiptVisible: boolean = true;
    receivedDate: Date = null;
    resolveResponse: PendingApplicationPayment[] | HttpErrorResponse;
    routeSubscription: Subscription;
    saveSubscription: Subscription = new Subscription();
    selectedType: any = { id: "select", display: "--Select--" };
    unpaidStatuses: any[] = [PaymentStatus.Pending, PaymentStatus.Overdue, PaymentStatus.Partial];

    payAllDialog = new Dialog("This will apply all unapplied funds to all unpaid applications, in order of oldest applications first, descending order of the fee amount due. Do you wish to continue?", [new DialogButton("Yes", true, () => this.payAll()), new DialogButton("No", false, () => this.onDialogClose())]);

    @ViewChild('receivedOn') private _receivedDatePicker: CommonDatePickerComponent;
    @ViewChild('payButton') private _payButton: ElementRef;

    get availableFunds(): number {
        let funds = this.undisbursedCashPayments.cashAmount;
        for (let i = 0; i < this.undisbursedNoncashPayments.instruments.length; i++) {
            const instrument = this.undisbursedNoncashPayments.instruments[i];
            funds = funds + instrument.instrumentAmount - instrument.appliedAmount;
        }
        return funds;
    }

    get disableAdd(): boolean {
        if (this.selectedType.id === "select") return true;
        if (!this.paymentAmount || this.paymentAmount <= 0) return true;
        if (!this.disableItemNumber(this.selectedType.id) && !this.itemNumber) return true;
        if (this.receivedDate == null) return true;
        return false;
    }
  
    get disablePayAll(): boolean {
        return this.undisbursedCashPayments.cashAmount === 0 && this.undisbursedNoncashPayments.instruments.length === 0;
    }  

    get hasPartials(): boolean {
        const partials = this.undisbursedNoncashPayments.instruments.filter(i => i.appliedAmount !== 0);
        return partials.length > 0;
    }

    get hasUnappliedPayments(): boolean {
        return this.undisbursedNoncashPayments.instruments.length > 0 || this.undisbursedCashPayments.cashAmount > 0;
    }

    get paymentsApplied(): boolean {
        if (this.disbursedCashPayments.cashAmount > 0 ||
            this.disbursedNoncashPayments.instruments.length > 0) return true;
        return this.hasPartials;
    }

    get havePayment(): boolean {
        return this.undisbursedNoncashPayments.instruments.length > 0 || this.undisbursedCashPayments.cashAmount > 0;
    }

    get isCashPayment(): boolean {
        return this.currentPaymentTypeId === 'PaidCash';
    }

    get isCheckPayment(): boolean {
        return this.currentPaymentTypeId === 'LocalCheck';
    }

    get isMoneyOrderPayment(): boolean {
        return this.currentPaymentTypeId === 'MoneyOrder';
    }
    private _now: Date = null;
    get now(): Date {
        if (this._now == null) {
            this._now = new Date();
            this._now.setDate(this._now.getDate() + 1);
        }
        return this._now;
    }

    disbursedCashPayments: PaymentTypeDisplay = new PaymentTypeDisplay(genId(), true);
    disbursedNoncashPayments: PaymentTypeDisplay = new PaymentTypeDisplay(genId(), false);
    undisbursedCashPayments: PaymentTypeDisplay = new PaymentTypeDisplay(genId(), true);
    undisbursedNoncashPayments: PaymentTypeDisplay = new PaymentTypeDisplay(genId(), false);

    constructor(
        public readonly route: ActivatedRoute,
        dialogService: DynamicDialogService,
        public subHeaderService: SubHeaderService,
        public layoutService: LayoutService,
        auth: AuthService,
        private flowService: FlowInstanceService,
        private loader: LoaderService,
        private appService: ApplicationsService
    ) { super(dialogService, auth); }

    ngOnInit() {
        this.layoutService.update('PaymentProcessingComponent');
        this.confirmDialog = new Dialog("<p class=\"bold\">Warning: Leaving this page without issuing receipts will remove all payments applied.</p><p>A receipt was not issued for some or all payments made, or the full balance of the monies entered has not been applied.</p>", [new DialogButton("Cancel", false, () => this.onLeaveCancel()), new DialogButton("Leave Page", true, () => this.onLeaveConfirm())]);
        this.routeSubscription = this.route.data.subscribe(data => {
			this.resolveResponse = data.payments;
            if (this.resolveResponse instanceof HttpErrorResponse) {
                console.log(`Error occurred in resolver - ${this.resolveResponse}`);
            } else {
                this.pendingPayments = this.resolveResponse as PendingApplicationPayment[];
                this.organization = this.pendingPayments[0].organization;
                this.groupPaymentsByApplication();
            }
            this.paymentsLoading$.next(false);
        });
        this.subHeaderService.updateSubHeader(this.subHeader);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
        if (this.saveSubscription != null) this.saveSubscription.unsubscribe();
    }

    acceptPayment(e: Event): void {
        this.currentPaymentTypeId = this.selectedType.id;
        if (this.selectedType.id != PaymentType.PaidCash) {
            const instrument = { id: genId(), instrumentType: this.selectedType.id, instrumentAmount: this.paymentAmount, token: this.itemNumber, appliedAmount: 0, receivedDate: this.receivedDate } as PaymentInstrument;
            this.undisbursedNoncashPayments.instruments.push(instrument);
        } else {
            const instrument = { id: genId(), instrumentType: this.selectedType.id, instrumentAmount: this.paymentAmount, token: null, appliedAmount: 0, receivedDate: this.receivedDate } as PaymentInstrument;
            this.undisbursedCashPayments.instruments.push(instrument);
        }
        this.paymentAmount = undefined;
        this.selectedType = { id: "select", display: "--Select--" };
        this.itemNumber = undefined;
        this.allowDeactivate.next(false);
    }

    alertInvalid(record: PendingApplicationPayment): boolean {
        let undisbursedAmount = this.getUndisbursed();
        let alertInvalid = (record.unappliedFunds === null || record.unappliedFunds === undefined || (record.unappliedFunds >= 0.01 && record.unappliedFunds <= undisbursedAmount));
        return alertInvalid;
    }

    confirmPayAll(): void {
        this.payAllDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.payAllDialog);
    }

    confirmPayAllForApplication(application: string): void {
        let payAllApplicationDialog = new Dialog(`This will apply all unapplied funds to application ${application} in descending order of the fee amount due until all funds are applied or all fees are paid. Do you wish to continue?`, [new DialogButton("Yes", true, () => this.payAllForApplication(application)), new DialogButton("No", false, () => this.onDialogClose())]);
        payAllApplicationDialog.toggleDialog(true);
        this.dialogService.updateDialog(payAllApplicationDialog);
    }


    disableItemNumber(itemType: string): boolean {
        return this.itemsRequiringNumber.findIndex(i => i === itemType) === -1;
    }

    disablePay(record: PendingApplicationPayment): boolean {
        return !this.alertInvalid(record) || !record.unappliedFunds || record.unappliedFunds <= 0 || this.blockPaymentStatuses.indexOf(record.paymentStatus) !== -1;
    }



    disablePayForApplication(application: string): boolean {
        if (this.disablePayAll) return true;
        const records = this.applicationPayments.get(application);
        const unpaid = records.filter(r => this.blockPaymentStatuses.indexOf(r.paymentStatus) === -1);
        return unpaid.length === 0;
    }

    getApplicationDateForApplication(application: string): string {
        const pmts = this.applicationPayments.get(application);
        return new Date(pmts[0].applicationDate).toLocaleDateString("en-US");
    }

    getApplicationTypeForApplication(application: string): string {
        const pmts = this.applicationPayments.get(application);
        return pmts[0].applicationType;
    }

    getDescription(record: ApplicationPayment): string {
        if (!record.description) return record.accountDescription;
        if (!record.accountDescription) return record.description;
        if (record.accountDescription.indexOf(record.description) !== -1)
            return record.accountDescription;
        return `${record.description} - ${record.accountDescription}`;
    }

    getPaymentsByApplication(application: string): ApplicationPayment[] {
        return this.applicationPayments.get(application);
    }

    getTotalDueForApplication(application: string): number {
        const payments = this.getPaymentsByApplication(application);
        let total = 0;
        payments.forEach(p => total = total + p.amountDue);
        return total;
    }

    getUndisbursed(): number {
        let undisbursed = 0;
        var amountPaid = 0;

        undisbursed += this.undisbursedCashPayments.cashAmount;

        for (let i = 0; i < this.undisbursedNoncashPayments.instruments.length; i++) {
            undisbursed += this.undisbursedNoncashPayments.instruments[i].instrumentAmount;
            amountPaid += this.undisbursedNoncashPayments.instruments[i].appliedAmount;
        }

        if (undisbursed > 0) {
            undisbursed -= amountPaid;
        }

        return undisbursed;
    }

    getUnpaid(): PendingApplicationPayment[] {
        const allUnpaid = [];
        for (let a = 0; a < this.applicationList.length; a++) {
            const list = this.getPaymentsByApplication(this.applicationList[a]);
            const unpaid = list.filter(p => this.unpaidStatuses.indexOf(p.paymentStatus) !== -1);
            unpaid.forEach(u => allUnpaid.push(u));
        }
        return allUnpaid;
    }

    groupPaymentsByApplication(): void {
        this.pendingPayments.forEach(payment => {
            let paymentList: PendingApplicationPayment[];
            if (!this.applicationPayments.has(payment.applicationNumber)) {
            this.applicationList.push(payment.applicationNumber);
            paymentList = [];
            } else {
            paymentList = this.applicationPayments.get(payment.applicationNumber);
            }
            paymentList.push(payment);
            this.applicationPayments.set(payment.applicationNumber, paymentList);
        });
    }

    pay(record: PendingApplicationPayment, totalUndisbursed: number): void {
        if (totalUndisbursed == undefined || totalUndisbursed == null)
            totalUndisbursed = this.getUndisbursed()
        let paid = 0;
        if (record.unappliedFunds > totalUndisbursed) {
            paid = totalUndisbursed;
        } else {
            paid = (record.unappliedFunds > record.amountDue) ? record.amountDue : record.unappliedFunds;
        }
        record.amountPaid = record.amountPaid + paid;
        record.amountDue = record.amountDue - paid;
        let yetToDisburse = paid;
        if (this.cashFirst) {
            if (this.undisbursedCashPayments.cashAmount > 0) {
            yetToDisburse = this.payUsingCash(yetToDisburse, record);
            }
            if (yetToDisburse > 0 && this.undisbursedNoncashPayments.instruments.length > 0) {
            yetToDisburse = this.payUsingInstrument(yetToDisburse, record);
            }

        } else {
            if (this.undisbursedNoncashPayments.instruments.length > 0) {
            yetToDisburse = this.payUsingInstrument(yetToDisburse, record);
            }
            if (yetToDisburse > 0 && this.undisbursedCashPayments.cashAmount > 0) {
            yetToDisburse = this.payUsingCash(yetToDisburse, record);
            }
        }
        const feeAmount = record.fee;//parseFloat(record.fee);
        if (record.amountPaid === feeAmount) {
            record.paymentStatus = PaymentStatus.PendingReceipt;
        } else if (record.amountPaid < feeAmount) {
            record.paymentStatus = PaymentStatus.Partial;
        } else if (record.amountPaid > feeAmount) {
            record.paymentStatus = PaymentStatus.Overpaid;
        }
        record.unappliedFunds = undefined;
        //should we reset the payment id?
        this.checkCurrentPaymentId();
    }

    payUsingCash(toDisburse: number, record: PendingApplicationPayment): number {
        let amounts: number[] = [];
        let instrumentsUsed: PaymentInstrument[] = [];
        let partialInstruments = this.undisbursedCashPayments.instruments.filter(i => i.appliedAmount < i.instrumentAmount);
        let greaterInstruments = this.undisbursedCashPayments.instruments.filter(i => i.appliedAmount === 0 && i.instrumentAmount >= toDisburse);
        let remaining = this.undisbursedCashPayments.instruments.filter(i => i.appliedAmount === 0 && i.instrumentAmount < toDisburse);
        for (let p = 0; p < partialInstruments.length; p++) {
            const equalPartials = partialInstruments.filter(i => (i.instrumentAmount - i.appliedAmount) === toDisburse);
            if (equalPartials.length > 0) {
                equalPartials[0].appliedAmount = equalPartials[0].appliedAmount + toDisburse;
                const index = instrumentsUsed.findIndex(c => c.id === equalPartials[0].id);
                if (index !== -1) {
                    instrumentsUsed.splice(index, 1);
                    amounts.splice(index, 1);
                }
                amounts.push(toDisburse);
                instrumentsUsed.push(equalPartials[0]);
                toDisburse = 0;
            }
            if (toDisburse > 0) {
                let greaterPartials = partialInstruments.filter(i => (i.instrumentAmount - i.appliedAmount) > toDisburse);
                if (greaterPartials.length > 0) {
                    greaterPartials = greaterPartials.sort((a, b) => {
                        const aRemaining = a.instrumentAmount - a.appliedAmount;
                        const bRemaining = b.instrumentAmount - b.appliedAmount;
                        if (aRemaining < bRemaining) return -1;
                        if (aRemaining > bRemaining) return 1;
                        return 0;
                    });
                    greaterPartials[0].appliedAmount = greaterPartials[0].appliedAmount + toDisburse;
                    const index = instrumentsUsed.findIndex(c => c.id === equalPartials[0].id);
                    if (index !== -1) {
                        instrumentsUsed.splice(index, 1);
                        amounts.splice(index, 1);
                    }
                    amounts.push(toDisburse);
                    instrumentsUsed.push(greaterPartials[0]);
                    toDisburse = 0;
                }
            }
            if (toDisburse > 0) {
                let remainingPartials = partialInstruments.filter(i => (i.instrumentAmount - i.appliedAmount) < toDisburse);
                if (remainingPartials.length > 0) {
                    remainingPartials = remainingPartials.sort((a, b) => {
                        const aRemaining = a.instrumentAmount - a.appliedAmount;
                        const bRemaining = b.instrumentAmount - b.appliedAmount;
                        if (aRemaining < bRemaining) return -1;
                        if (aRemaining > bRemaining) return 1;
                        return 0;
                    });
                    for (let r = 0; r < remainingPartials.length; r++) {
                        const remainder = remainingPartials[r].instrumentAmount - remainingPartials[r].appliedAmount;
                        if (toDisburse >= remainder) {
                            remainingPartials[r].appliedAmount = remainingPartials[r].appliedAmount + remainder;
                            toDisburse = toDisburse - remainder;
                            const index = instrumentsUsed.findIndex(c => c.id === equalPartials[0].id);
                            if (index !== -1) {
                                instrumentsUsed.splice(index, 1);
                                amounts.splice(index, 1);
                            }
                            amounts.push(remainder);
                            instrumentsUsed.push(remainingPartials[r]);

                        } else {
                            remainingPartials[r].appliedAmount = remainingPartials[r].appliedAmount + toDisburse;
                            const index = instrumentsUsed.findIndex(c => c.id === equalPartials[0].id);
                            if (index !== -1) {
                                instrumentsUsed.splice(index, 1);
                                amounts.splice(index, 1);
                            }
                            amounts.push(toDisburse);
                            instrumentsUsed.push(remainingPartials[r]);
                            toDisburse = 0;
                        }
                    }
                }
            }
        }
        if (toDisburse > 0 && greaterInstruments.length > 0) {
            greaterInstruments = greaterInstruments.sort((a, b) => {
                if (a.instrumentAmount > b.instrumentAmount) return - 1;
                if (b.instrumentAmount > a.instrumentAmount) return 1;
                return 0;
            });
            greaterInstruments[0].appliedAmount = toDisburse;
            if (instrumentsUsed.findIndex(c => c.id === greaterInstruments[0].id) === -1) {
                amounts.push(toDisburse);
                instrumentsUsed.push(greaterInstruments[0]);
            }
            toDisburse = 0;
        }
        if (toDisburse > 0) {
            remaining = remaining.sort((a, b) => {
                if (a.instrumentAmount < b.instrumentAmount) return -1;
                if (b.instrumentAmount < a.instrumentAmount) return 1;
                return 0;
            });
            for (let r = 0; r < remaining.length; r++) {
                if (remaining[r].instrumentAmount > toDisburse) {
                    remaining[r].appliedAmount = toDisburse;
                    amounts.push(toDisburse);
                } else {
                    remaining[r].appliedAmount = remaining[r].instrumentAmount;
                    amounts.push(remaining[r].instrumentAmount);
                    toDisburse = toDisburse - remaining[r].appliedAmount;
                }
                if (instrumentsUsed.findIndex(c => c.id === remaining[r].id) === -1) {
                    instrumentsUsed.push(remaining[r]);
                }
            }
        }
        for (let c = 0; c < instrumentsUsed.length; c++) {
            const inst = instrumentsUsed[c];
            const instrumentToken = writeToken(amounts[c], inst);

            let cashPayment: Payment = {
                applicationPaymentId: record.id,
                amount: amounts[c],
                instrumentAmount: inst.instrumentAmount,
                paymentType: inst.instrumentType,
                tokenId: null,
                token: instrumentToken,
                paymentReceivedDate: inst.receivedDate
            };
            record.paymentRecords.push(cashPayment);

            if (inst.instrumentAmount === inst.appliedAmount) {
                const instIndex = this.undisbursedCashPayments.instruments.findIndex(f => f.token === inst.token);
                this.undisbursedCashPayments.instruments.splice(instIndex, 1);
                this.disbursedCashPayments.instruments.push(inst);
            }
        }

        //should we reset the payment id?
        this.checkCurrentPaymentId();

        return toDisburse;
    }

    payUsingInstrument(toDisburse: number, record: PendingApplicationPayment): number {
        let amounts: number[] = [];
        let instrumentsUsed: PaymentInstrument[] = [];
        let partialInstruments = this.undisbursedNoncashPayments.instruments.filter(i => i.appliedAmount < i.instrumentAmount);
        let greaterInstruments = this.undisbursedNoncashPayments.instruments.filter(i => i.appliedAmount === 0 && i.instrumentAmount >= toDisburse);
        let remaining = this.undisbursedNoncashPayments.instruments.filter(i => i.appliedAmount === 0 && i.instrumentAmount < toDisburse);
        for (let p = 0; p < partialInstruments.length; p++) {
            const equalPartials = partialInstruments.filter(i => (i.instrumentAmount - i.appliedAmount) === toDisburse);
            if (equalPartials.length > 0) {
                equalPartials[0].appliedAmount = equalPartials[0].appliedAmount + toDisburse;
                const index = instrumentsUsed.findIndex(c => c.id === equalPartials[0].id);
                if (index !== -1) {
                    instrumentsUsed.splice(index, 1);
                    amounts.splice(index, 1);
                }
                amounts.push(toDisburse);
                instrumentsUsed.push(equalPartials[0]);
                toDisburse = 0;
            }
            if (toDisburse > 0) {
                let greaterPartials = partialInstruments.filter(i => (i.instrumentAmount - i.appliedAmount) > toDisburse);
                if (greaterPartials.length > 0) {
                    greaterPartials = greaterPartials.sort((a, b) => {
                        const aRemaining = a.instrumentAmount - a.appliedAmount;
                        const bRemaining = b.instrumentAmount - b.appliedAmount;
                        if (aRemaining < bRemaining) return -1;
                        if (aRemaining > bRemaining) return 1;
                        return 0;
                    });
                    greaterPartials[0].appliedAmount = greaterPartials[0].appliedAmount + toDisburse;
                    const index = instrumentsUsed.findIndex(c => c.id === equalPartials[0].id);
                    if (index !== -1) {
                        instrumentsUsed.splice(index, 1);
                        amounts.splice(index, 1);
                    }
                    amounts.push(toDisburse);
                    instrumentsUsed.push(greaterPartials[0]);
                    toDisburse = 0;
                }
            }
            if (toDisburse > 0) {
                let remainingPartials = partialInstruments.filter(i => (i.instrumentAmount - i.appliedAmount) < toDisburse);
                if (remainingPartials.length > 0) {
                        remainingPartials = remainingPartials.sort((a, b) => {
                        const aRemaining = a.instrumentAmount - a.appliedAmount;
                        const bRemaining = b.instrumentAmount - b.appliedAmount;
                        if (aRemaining < bRemaining) return -1;
                        if (aRemaining > bRemaining) return 1;
                        return 0;
                    });
                    for (let r = 0; r < remainingPartials.length; r++) {
                        const remainder = remainingPartials[r].instrumentAmount - remainingPartials[r].appliedAmount;
                        if (toDisburse >= remainder) {
                            remainingPartials[r].appliedAmount = remainingPartials[r].appliedAmount + remainder;
                            toDisburse = toDisburse - remainder;
                            const index = instrumentsUsed.findIndex(c => c.id === equalPartials[0].id);
                            if (index !== -1) {
                                instrumentsUsed.splice(index, 1);
                                amounts.splice(index, 1);
                            }
                            amounts.push(remainder);
                            instrumentsUsed.push(remainingPartials[r]);

                        } else {
                            remainingPartials[r].appliedAmount = remainingPartials[r].appliedAmount + toDisburse;
                            const index = instrumentsUsed.findIndex(c => c.id === equalPartials[0].id);
                            if (index !== -1) {
                                instrumentsUsed.splice(index, 1);
                                amounts.splice(index, 1);
                            }
                            amounts.push(toDisburse);
                            instrumentsUsed.push(remainingPartials[r]);
                            toDisburse = 0;
                        }
                    }
                }
            }
        }
        if (toDisburse > 0 && greaterInstruments.length > 0) {
            greaterInstruments = greaterInstruments.sort((a, b) => {
            if (a.instrumentAmount > b.instrumentAmount) return - 1;
            if (b.instrumentAmount > a.instrumentAmount) return 1;
            return 0;
            });
            greaterInstruments[0].appliedAmount = toDisburse;
            if (instrumentsUsed.findIndex(c => c.id === greaterInstruments[0].id) === -1) {
            amounts.push(toDisburse);
            instrumentsUsed.push(greaterInstruments[0]);
            }
            toDisburse = 0;
        }
        if (toDisburse > 0) {
            remaining = remaining.sort((a, b) => {
            if (a.instrumentAmount < b.instrumentAmount) return -1;
            if (b.instrumentAmount < a.instrumentAmount) return 1;
            return 0;
            });
            for (let r = 0; r < remaining.length; r++) {
            if (remaining[r].instrumentAmount > toDisburse) {
                remaining[r].appliedAmount = toDisburse;
                amounts.push(toDisburse);
            } else {
                remaining[r].appliedAmount = remaining[r].instrumentAmount;
                amounts.push(remaining[r].instrumentAmount);
                toDisburse = toDisburse - remaining[r].appliedAmount;
            }
            if (instrumentsUsed.findIndex(c => c.id === remaining[r].id) === -1) {
                instrumentsUsed.push(remaining[r]);
            }
            }
        }
        for (let c = 0; c < instrumentsUsed.length; c++) {
            const inst = instrumentsUsed[c];
            const instrumentToken = writeToken(amounts[c], inst);

            let cashPayment: Payment = {
                applicationPaymentId: record.id,
                amount: amounts[c],
                instrumentAmount: inst.instrumentAmount,
                paymentType: inst.instrumentType,
                tokenId: inst.token.replace("#", ""),
                token: instrumentToken,
                paymentReceivedDate: inst.receivedDate
            };
            record.paymentRecords.push(cashPayment);

            if (inst.instrumentAmount === inst.appliedAmount) {
                const instIndex = this.undisbursedNoncashPayments.instruments.findIndex(f => f.token === inst.token);
                this.undisbursedNoncashPayments.instruments.splice(instIndex, 1);
                this.disbursedNoncashPayments.instruments.push(inst);
            }
        }

        //should we reset the payment id?
        this.checkCurrentPaymentId();

        return toDisburse;
    }

    payAll(): void {
        let applicationSort = [];
        this.applicationPayments.forEach(pmts => {
            const firstPayment = pmts[0];
            var obj = {
                application: firstPayment.applicationNumber,
                date: new Date(firstPayment.applicationDate)
            };
            applicationSort.push(obj);
        });
        applicationSort = applicationSort.sort((a, b) => {
            const aTime = a.date.getTime();
            const bTime = b.date.getTime();
            if (bTime > aTime) return -1;
            if (bTime < aTime) return 1;
            return 0;
        });

        for (let n = 0; n < applicationSort.length; n++) {
            this.payByApplication(applicationSort[n].application);
            if (this.getUndisbursed() === 0) break;
        }
        this.checkCurrentPaymentId();
    }

    payAllForApplication(application: string): void {
        this.application = application;
        this.payByApplication(this.application);
        this.checkCurrentPaymentId();
    }

    payByApplication(application: string): void {
        let payments = this.applicationPayments.get(application);
        payments = payments.sort((a, b) => {
            const bFee = b.fee;// parseFloat(b.fee);
            const aFee = a.fee;//parseFloat(a.fee);
            if (aFee > bFee) return -1;
            if (bFee > aFee) return 1;
            return 0;
        });
        let unpaid = payments.filter(p => this.blockPaymentStatuses.indexOf(p.paymentStatus) === -1);

        let undisbursedChecked = false;
        let undisbursed = 0;
        while (unpaid.length > 0 && (!undisbursedChecked || undisbursed > 0)) {
            undisbursed = this.getUndisbursed();
            undisbursedChecked = true;
            if (undisbursed > 0) {
            unpaid[0].unappliedFunds = (unpaid[0].amountDue <= undisbursed) ? unpaid[0].amountDue : undisbursed;
            this.pay(unpaid[0], undisbursed);
            unpaid = payments.filter(p => this.blockPaymentStatuses.indexOf(p.paymentStatus) === -1);
            }
        }
        payments = payments.sort((a, b) => {
            const bLast = parseInt(b.id.substring(b.id.length - 4), 10);
            const aLast = parseInt(a.id.substring(a.id.length - 4), 10);
            if (bLast > aLast) return -1;
            if (aLast < bLast) return 1;
            return 0;
        });
        this.applicationPayments.set(application, payments);
        //should we reset the payment id?
        this.checkCurrentPaymentId();
    }

    checkCurrentPaymentId(): void {
        if (!this.havePayment) {
            this.currentPaymentTypeId = 'select';            
        }
    }

    paymentAccordionClick(): void {
        this.paymentVisible = !this.paymentVisible;
    }

    receiptAccordionClick(): void {
        this.receiptVisible = !this.receiptVisible;
    }

    receivedDateChanged(evt: Date): void {
        this.receivedDate = evt;
    }

    setPrecision(payment: PendingApplicationPayment): void {
        let value: number = 0;
        if (payment.unappliedFunds != null) value = Number(payment.unappliedFunds.toFixed(2));
        payment.unappliedFunds = value;
    }

    showPaymentEntrySection(index: number): void {
        this.paymentEntrySection = index;
    }   

    public onDialogClose(): void {
        console.log("payment application cancelled");
    }

    onLeaveConfirm(): void {
        this.allowDeactivate.next(true);
    }

    onLeaveCancel(): void {

    }

    onReceiptIssued(payments: ApplicationPayment[]): void {
        let recvdApps = payments.filter(p => p.paymentReceivedDate != null && (new Date(p.paymentReceivedDate)).toLocaleDateString().length > 0);
        if (recvdApps.length > 0) {

            let delay$: Observable<any> = of(recvdApps).pipe(
                delay(4000),
                map((pmts: ApplicationPayment[]) => {
                    let reqs: PaymentReceivedDateRequest[] = [];
                    pmts.forEach(p => {
                        let existing = reqs.find(r => r.paymentReceivedDate == p.paymentReceivedDate);
                        if (existing != null) {
                            existing.applicationNumbers.push(p.applicationNumber);
                        } else {
                            let req: PaymentReceivedDateRequest = {
                                applicationNumbers: [p.applicationNumber],
                                flowInstanceIds: [p.workflowId],
                                paymentReceivedDate: p.paymentReceivedDate,
                                isElectronicPayment: false
                            } as PaymentReceivedDateRequest;
                            reqs.push(req);
                        }

                    })
                    return reqs;
                }),
                mergeMap((reqs: PaymentReceivedDateRequest[]) => {
                    this.loader.toggleLoader(true);
                    let obs$: Observable<any>[] = [];
					reqs.forEach(r => {
						if (isNaN(Number(r.flowInstanceIds[0]))) {
                            obs$.push(this.flowService.setPaymentReceivedDates(r));
                        } else {
                            obs$.push(this.appService.recordPaymentDate(r));
                        }
                    });
                    if (obs$.length > 0) {
                        return forkJoin(obs$);
                    } else {
                        return of(null);
                    }
                })
            );
            let subs$: Subscription = delay$.subscribe(
                (req: any) => {
                    this.loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                }
            );
            this.saveSubscription.add(subs$);
        } else {
            this.loader.toggleLoader(false);
        }
       
        this.allowDeactivate.next(!this.hasUnappliedPayments);
    }

}
